import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { client, getToken, setToken } from "@/lib/api";
import OverviewTab from "@/pages/admin/OverviewTab";
import DetailsTab from "@/pages/admin/DetailsTab";
import ListEditor from "@/pages/admin/ListEditor";
import FamiliesTab from "@/pages/admin/FamiliesTab";
import PaymentsTab from "@/pages/admin/PaymentsTab";
import PhotosTab from "@/pages/admin/PhotosTab";
import OrganizersTab from "@/pages/admin/OrganizersTab";
import BackupTab from "@/pages/admin/BackupTab";
import { LISTS } from "@/pages/admin/listConfigs";

const TABS = [
  ["overview", "Overview"], ["details", "Details & Fees"], ["schedule", "Schedule"], ["questions", "Form Questions"],
  ["activities", "Activities"], ["teams", "Teams"], ["families", "Families"], ["payments", "Payments"],
  ["photos", "Photos"], ["organizers", "Organizers"], ["backup", "Backup"],
];

function Body({ tab, go }) {
  if (tab === "overview") return <OverviewTab go={go} />;
  if (tab === "details") return <DetailsTab />;
  if (LISTS[tab]) return <ListEditor key={tab} kind={tab} config={LISTS[tab]} />;
  if (tab === "families") return <FamiliesTab />;
  if (tab === "payments") return <PaymentsTab />;
  if (tab === "photos") return <PhotosTab />;
  if (tab === "organizers") return <OrganizersTab />;
  return <BackupTab />;
}

export default function AdminDashboard() {
  const [tab, setTab] = useState(() => sessionStorage.getItem("fanene_admin_tab") || "overview");
  const [me, setMe] = useState(null);
  useEffect(() => { if (getToken("admin")) client("admin").get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  useEffect(() => sessionStorage.setItem("fanene_admin_tab", tab), [tab]);
  if (!getToken("admin")) return <Navigate to="/organizer/login" replace />;

  return (
    <div className="min-h-screen bg-[#F3EEE6]">
      <header className="sticky top-0 z-40 bg-stone-900 text-amber-50 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <p className="font-heading font-bold text-lg sm:text-xl"><span className="text-amber-400">Fanene</span> · Organizer area</p>
          <div className="flex items-center gap-2">
            <span data-testid="admin-current-user" className="hidden md:inline text-sm text-amber-100/80">{me?.email}</span>
            <Link to="/" target="_blank" data-testid="admin-view-site-link" className="hidden sm:flex items-center gap-1 px-3 py-2 rounded-full hover:bg-white/10 text-sm font-semibold"><ExternalLink className="w-4 h-4" />View site</Link>
            <button data-testid="admin-logout-button" onClick={() => { setToken("admin", null); window.location.assign("/organizer/login"); }} className="flex items-center gap-1 px-3 py-2 rounded-full border border-amber-200/30 hover:bg-white/10 text-sm font-semibold"><LogOut className="w-4 h-4" />Sign out</button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-2 sm:px-4 flex gap-1 overflow-x-auto no-scrollbar pb-2">
          {TABS.map(([id, label]) => (
            <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold ${tab === id ? "bg-amber-400 text-stone-900" : "text-amber-50/85 hover:bg-white/10"}`}>{label}</button>
          ))}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8"><Body tab={tab} go={setTab} /></main>
    </div>
  );
}
